import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants.js';
import { logStage, STAGES } from '../utils/IPLogger.js';

export default class CutsceneScene extends Phaser.Scene {
  constructor() {
    super({ key: 'CutsceneScene' });
    this.currentLine = 0;
    this.currentChar = 0;
    this.currentPart = 0;
    this.textObjects = [];
    this.cursor = null;
    this.isTyping = false;
    this.music = null;
  }

  create() {
    // Log arrival at level 1 cutscene
    logStage(STAGES.LEVEL_1_CUTSCENE);

    // Black background
    this.cameras.main.setBackgroundColor('#000000');

    // All story parts
    this.storyParts = [
      // Part 1: The Outbreak
      [
        { text: 'NEW METRO CITY... 1987', delay: 50, pause: 1500, color: '#00ff00' },
        { text: '', delay: 0, pause: 400 },
        { text: 'THEY CALLED IT THE CITY OF TOMORROW.', delay: 35, color: '#ffffff' },
        { text: 'A BEACON OF PROGRESS.', delay: 35, pause: 800, color: '#ffffff' },
        { text: '', delay: 0, pause: 300 },
        { text: 'GENESYS LABORATORIES PROMISED', delay: 30, color: '#00ffff' },
        { text: 'A CURE FOR DEATH ITSELF.', delay: 30, pause: 1000, color: '#00ffff' },
        { text: '', delay: 0, pause: 400 },
        { text: 'THEY FOUND SOMETHING WORSE.', delay: 55, pause: 1800, color: '#ff0000' },
        { text: '', delay: 0, pause: 600 },
        { text: 'OCTOBER 31ST. 0300 HOURS.', delay: 35, color: '#ffff00' },
        { text: 'PATIENT ZERO ESCAPED CONTAINMENT.', delay: 35, pause: 1200, color: '#ffff00' },
        { text: '', delay: 0, pause: 400 },
        { text: 'BY SUNRISE...', delay: 70, pause: 600, color: '#ff6600' },
        { text: 'THE DOWNTOWN SECTOR WAS LOST.', delay: 45, pause: 2000, color: '#ff0000' },
      ],
      // Part 2: Duke Steele's recruitment
      [
        { text: 'THE AGENCY FOUND HIM THREE DAYS LATER.', delay: 35, pause: 1000, color: '#00ffff' },
        { text: '', delay: 0, pause: 400 },
        { text: 'SERGEANT DUKE STEELE.', delay: 45, pause: 800, color: '#ffffff' },
        { text: 'THREE TOURS. BLACK OPS. CLASSIFIED.', delay: 30, pause: 1000, color: '#ffffff' },
        { text: '', delay: 0, pause: 400 },
        { text: 'THE ODDS OF RESTORING THE CITY', delay: 30, color: '#888888' },
        { text: 'WERE GRIM.', delay: 50, pause: 800, color: '#888888' },
        { text: '', delay: 0, pause: 300 },
        { text: 'BUT HE WAS THEIR BEST SHOT.', delay: 40, pause: 2000, color: '#00ff00' },
      ],
      // Part 3: The real reason
      [
        { text: 'WHAT THEY DIDN\'T KNOW...', delay: 50, pause: 1200, color: '#ff00ff' },
        { text: '', delay: 0, pause: 500 },
        { text: 'HE WASN\'T DOING THIS FOR THEM.', delay: 35, pause: 800, color: '#ffffff' },
        { text: 'OR FOR HIS CITY.', delay: 40, pause: 1200, color: '#ffffff' },
        { text: '', delay: 0, pause: 600 },
        { text: 'HE WAS DOING IT FOR JESSIE.', delay: 45, pause: 1500, color: '#ff6699' },
        { text: '', delay: 0, pause: 400 },
        { text: 'THE LOVE OF HIS LIFE.', delay: 40, pause: 1000, color: '#ff6699' },
        { text: 'TRAPPED IN AN IMPOSSIBLE SITUATION.', delay: 35, pause: 1200, color: '#ff6699' },
        { text: '', delay: 0, pause: 500 },
        { text: 'HER HEART... ZOMBIFIED.', delay: 60, pause: 2500, color: '#ff0000' },
      ],
      // Part 4: The question
      [
        { text: 'HE KNEW HE COULD DEFEAT THE ZOMBIES.', delay: 35, pause: 1200, color: '#00ff00' },
        { text: '', delay: 0, pause: 600 },
        { text: 'BUT COULD HE RESTORE HER HEART?', delay: 50, pause: 3500, color: '#ff00ff' },
      ],
    ];

    // Create blinking cursor
    this.cursor = this.add.text(40, 60, '_', {
      fontFamily: 'Courier New',
      fontSize: '20px',
      fill: '#00ff00',
    });

    // Blink cursor
    this.cursorTween = this.tweens.add({
      targets: this.cursor,
      alpha: { from: 1, to: 0 },
      duration: 300,
      yoyo: true,
      repeat: -1,
    });

    // CRT scanline effect overlay
    this.createScanlines();

    // Start background music
    this.music = this.sound.add('cutscene_music', {
      loop: true,
      volume: 0.5,
    });
    this.music.play();

    // Start typing after fade in
    this.cameras.main.fadeIn(500, 0, 0, 0);
    this.time.delayedCall(800, () => {
      this.startTypingPart(0);
    });

    // Skip on Enter or click
    this.input.keyboard.on('keydown-ENTER', () => this.skipCutscene());
    this.input.keyboard.on('keydown-SPACE', () => this.skipCutscene());
    this.input.keyboard.on('keydown-ESC', () => this.skipCutscene());

    // Skip text at bottom
    this.skipText = this.add.text(GAME_WIDTH - 20, GAME_HEIGHT - 20, 'PRESS ENTER TO SKIP', {
      fontFamily: 'Courier New',
      fontSize: '12px',
      fill: '#444444',
    });
    this.skipText.setOrigin(1, 1);
  }

  createScanlines() {
    const graphics = this.add.graphics();
    graphics.setDepth(100);

    // Draw horizontal scanlines
    for (let y = 0; y < GAME_HEIGHT; y += 3) {
      graphics.fillStyle(0x000000, 0.15);
      graphics.fillRect(0, y, GAME_WIDTH, 1);
    }
  }

  startTypingPart(partIndex) {
    this.currentPart = partIndex;
    this.currentLine = 0;
    this.currentChar = 0;
    this.isTyping = true;

    // Clear previous text objects
    this.textObjects.forEach(obj => obj.destroy());
    this.textObjects = [];

    // Reset cursor position
    this.cursor.setPosition(40, 60);
    this.cursor.setVisible(true);

    this.typeLine();
  }

  typeLine() {
    const currentStoryPart = this.storyParts[this.currentPart];

    if (this.currentLine >= currentStoryPart.length) {
      // Done with this part - transition to next
      this.transitionToNextPart();
      return;
    }

    const line = currentStoryPart[this.currentLine];
    const yPos = 60 + this.currentLine * 28;

    // Empty line - just pause
    if (line.text === '') {
      this.time.delayedCall(line.pause || 300, () => {
        this.currentLine++;
        this.typeLine();
      });
      return;
    }

    // Create text object for this line
    const textObj = this.add.text(40, yPos, '', {
      fontFamily: 'Courier New',
      fontSize: '20px',
      fill: line.color || '#ffffff',
    });
    this.textObjects.push(textObj);

    // Update cursor color to match text
    this.cursor.setFill(line.color || '#ffffff');

    // Type each character
    this.currentChar = 0;
    this.typeChar(textObj, line);
  }

  typeChar(textObj, line) {
    if (this.currentChar >= line.text.length) {
      // Finished this line
      const pauseTime = line.pause || 400;
      this.time.delayedCall(pauseTime, () => {
        this.currentLine++;
        this.typeLine();
      });
      return;
    }

    // Add next character
    textObj.setText(line.text.substring(0, this.currentChar + 1));

    // Update cursor position
    this.cursor.setPosition(40 + textObj.width + 2, textObj.y);

    this.currentChar++;

    // Schedule next character
    const charDelay = line.delay || 40;
    this.time.delayedCall(charDelay, () => {
      this.typeChar(textObj, line);
    });
  }

  transitionToNextPart() {
    this.isTyping = false;
    this.cursor.setVisible(false);

    // Check if there are more parts
    if (this.currentPart < this.storyParts.length - 1) {
      // Fade out current text with static glitch
      this.miniStaticTransition(() => {
        this.startTypingPart(this.currentPart + 1);
      });
    } else {
      // All parts done - finish cutscene
      this.finishCutscene();
    }
  }

  miniStaticTransition(callback) {
    // Quick static burst between parts
    const staticGraphics = this.add.graphics();
    staticGraphics.setDepth(150);

    let frame = 0;
    const maxFrames = 15;

    const staticTimer = this.time.addEvent({
      delay: 40,
      callback: () => {
        staticGraphics.clear();
        frame++;

        // Draw static noise
        const intensity = frame < maxFrames / 2 ? frame / (maxFrames / 2) : (maxFrames - frame) / (maxFrames / 2);
        for (let i = 0; i < intensity * 2000; i++) {
          const x = Phaser.Math.Between(0, GAME_WIDTH);
          const y = Phaser.Math.Between(0, GAME_HEIGHT);
          const brightness = Phaser.Math.Between(50, 200);
          const color = Phaser.Display.Color.GetColor(brightness, brightness, brightness);
          staticGraphics.fillStyle(color, Math.random() * 0.6);
          staticGraphics.fillRect(x, y, 2, 2);
        }

        // Horizontal glitch lines
        if (Math.random() < 0.4) {
          const glitchY = Phaser.Math.Between(0, GAME_HEIGHT);
          staticGraphics.fillStyle(0xffffff, 0.5);
          staticGraphics.fillRect(0, glitchY, GAME_WIDTH, Phaser.Math.Between(1, 4));
        }

        if (frame >= maxFrames) {
          staticTimer.destroy();
          staticGraphics.destroy();
          callback();
        }
      },
      loop: true,
    });

    // Fade out text during transition
    this.tweens.add({
      targets: this.textObjects,
      alpha: 0,
      duration: 300,
    });
  }

  finishCutscene() {
    this.isTyping = false;
    this.cursor.setVisible(false);
    this.skipText.setVisible(false);

    // Wait a moment then start static effect
    this.time.delayedCall(1500, () => {
      this.startStaticEffect();
    });
  }

  startStaticEffect() {
    // Create static noise overlay
    const staticGraphics = this.add.graphics();
    staticGraphics.setDepth(200);

    let staticIntensity = 0;
    const staticTimer = this.time.addEvent({
      delay: 50,
      callback: () => {
        staticGraphics.clear();
        staticIntensity += 0.025;

        // Draw random white noise pixels
        for (let i = 0; i < staticIntensity * 5000; i++) {
          const x = Phaser.Math.Between(0, GAME_WIDTH);
          const y = Phaser.Math.Between(0, GAME_HEIGHT);
          const brightness = Phaser.Math.Between(100, 255);
          const color = Phaser.Display.Color.GetColor(brightness, brightness, brightness);
          staticGraphics.fillStyle(color, Math.random() * 0.8);
          staticGraphics.fillRect(x, y, 2, 2);
        }

        // Add horizontal glitch lines
        if (Math.random() < staticIntensity * 0.4) {
          const glitchY = Phaser.Math.Between(0, GAME_HEIGHT);
          staticGraphics.fillStyle(0xffffff, 0.8);
          staticGraphics.fillRect(0, glitchY, GAME_WIDTH, Phaser.Math.Between(2, 8));
        }

        // When intensity is high enough, transition out
        if (staticIntensity >= 1) {
          staticTimer.destroy();
          this.transitionToGame(staticGraphics);
        }
      },
      loop: true,
    });

    // Fade out the text
    this.tweens.add({
      targets: this.textObjects,
      alpha: 0,
      duration: 1200,
    });
  }

  transitionToGame(staticGraphics) {
    // Fade out music
    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 1000,
      });
    }

    // Flash white
    this.cameras.main.flash(300, 255, 255, 255);

    // Fade to black
    this.time.delayedCall(300, () => {
      this.cameras.main.fadeOut(800, 0, 0, 0);
    });

    this.cameras.main.once('camerafadeoutcomplete', () => {
      if (this.music) {
        this.music.stop();
      }
      this.scene.start('GameScene');
    });
  }

  skipCutscene() {
    // Clean up timers
    this.time.removeAllEvents();
    this.tweens.killAll();

    // Stop music
    if (this.music) {
      this.music.stop();
    }

    // Quick fade to game
    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GameScene');
    });
  }
}
